import { useEffect, useRef } from 'react';
import { usePresencePingMutation } from '../store/api/trackingApi';
import { useThemeMode } from '../theme/ThemeModeContext';

const PING_MS = 60_000;        // pinga cada 60s
const IDLE_MS = 3 * 60_000;    // "inactivo" si no interactúa hace 3 min

/**
 * Heartbeat de PRESENCIA real. Pinga `/tracking/presence` solo cuando:
 *  - la pestaña está VISIBLE (Page Visibility API), y
 *  - el usuario interactuó (mouse/teclado/scroll/touch) hace menos de IDLE_MS.
 * Así el contador "Agentes en línea" refleja quién está de verdad, no pestañas abandonadas
 * que el polling de fondo mantenía "vivas" (bumpeando lastSeenAt). Ver User.lastActiveAt.
 */
export function usePresence(enabled: boolean) {
  const [ping] = usePresencePingMutation();
  const lastActivity = useRef(Date.now());
  // Modo de tema actual en un ref → el heartbeat lo lee sin re-crear el intervalo al togglear.
  const { mode } = useThemeMode();
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    if (!enabled) return;
    const bump = () => { lastActivity.current = Date.now(); };
    const evs: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity.current > IDLE_MS) return;
      ping({ themeMode: modeRef.current });  // fire-and-forget (+ modo de tema)
    };
    beat();  // marca presencia al montar (si estás mirando + activo)
    const id = window.setInterval(beat, PING_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') { bump(); beat(); } };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(id);
      evs.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, ping]);
}
